import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { profileStore } from '../stores/profileStore'
import { gamificationStore } from '../stores/gamificationStore'
import { mojilandStore } from '../stores/mojilandStore'
import { leaderboardStore } from '../stores/leaderboardStore'
import { hitLevels, getHitLevelInfo } from '../utils/levels'
import { pickRandom, shuffleArray } from '../utils/helpers'
import { resources } from '../utils/sets'
import { playSound, vibrate } from '../utils/sounds'
import GameLeaderboard from '../components/GameLeaderboard'

const BOARD_SIZE = 8
const INIT_MOVES = 15
const MIN_MATCH = 3
const SPEED_BONUS_THRESHOLD = 1500 // ms para bonus de velocidad

interface Cell {
  id: number
  icon: string
  isMatched: boolean
  isNew: boolean
  isShaking: boolean
  isFalling: boolean
  fallDistance: number // Cuántas filas cae
}

interface FloatingScore {
  id: number
  x: number
  y: number
  score: number
  emoji: string
  isCombo: boolean
}

// Obtener celdas adyacentes (incluyendo diagonales para más combos)
const getAdjacent = (index: number, size: number): number[] => {
  const row = Math.floor(index / size)
  const col = index % size
  const adjacent: number[] = []
  
  // Direcciones: arriba, abajo, izquierda, derecha
  if (row > 0) adjacent.push(index - size)
  if (row < size - 1) adjacent.push(index + size)
  if (col > 0) adjacent.push(index - 1)
  if (col < size - 1) adjacent.push(index + 1)
  
  // Diagonales: arriba-izq, arriba-der, abajo-izq, abajo-der
  if (row > 0 && col > 0) adjacent.push(index - size - 1)
  if (row > 0 && col < size - 1) adjacent.push(index - size + 1)
  if (row < size - 1 && col > 0) adjacent.push(index + size - 1)
  if (row < size - 1 && col < size - 1) adjacent.push(index + size + 1)
  
  return adjacent
}

// Encontrar matches conectados
const findMatches = (
  startIndex: number,
  cells: Cell[],
  size: number,
  visited = new Set<number>()
): number[] => {
  if (visited.has(startIndex)) return []
  visited.add(startIndex)
  const targetIcon = cells[startIndex]?.icon
  if (!targetIcon) return []
  const matches = [startIndex]
  const adjacent = getAdjacent(startIndex, size)
  for (const adjIndex of adjacent) {
    if (!visited.has(adjIndex) && cells[adjIndex]?.icon === targetIcon) {
      matches.push(...findMatches(adjIndex, cells, size, visited))
    }
  }
  return matches
}

// Aplicar gravedad: las celdas caen para llenar los huecos
const applyGravity = (cells: Cell[], matchedIndices: number[], iconSet: string[]): Cell[] => {
  const newCells = [...cells]
  
  // Marcar celdas como vacías
  for (const idx of matchedIndices) {
    newCells[idx] = { ...newCells[idx], icon: '', isMatched: true }
  }
  
  // Para cada columna, hacer caer las celdas
  for (let col = 0; col < BOARD_SIZE; col++) {
    // Obtener todas las celdas de esta columna de abajo a arriba
    const columnCells: { idx: number; cell: Cell }[] = []
    for (let row = BOARD_SIZE - 1; row >= 0; row--) {
      const idx = row * BOARD_SIZE + col
      columnCells.push({ idx, cell: newCells[idx] })
    }
    
    // Separar celdas vacías y llenas
    const filledCells = columnCells.filter(c => c.cell.icon !== '')
    const emptyCount = columnCells.length - filledCells.length
    
    // Reorganizar: primero las vacías (arriba), luego las llenas (abajo)
    let newRow = BOARD_SIZE - 1
    
    // Colocar las celdas existentes que caen
    for (const filled of filledCells) {
      const newIdx = newRow * BOARD_SIZE + col
      const oldRow = Math.floor(filled.idx / BOARD_SIZE)
      const fallDist = newRow - oldRow
      
      newCells[newIdx] = {
        ...filled.cell,
        id: newIdx,
        isFalling: fallDist > 0,
        fallDistance: fallDist,
        isMatched: false,
        isNew: false,
      }
      newRow--
    }
    
    // Rellenar las posiciones vacías de arriba con nuevas celdas
    for (let i = 0; i < emptyCount; i++) {
      const newIdx = newRow * BOARD_SIZE + col
      newCells[newIdx] = {
        id: newIdx,
        icon: pickRandom(iconSet),
        isMatched: false,
        isNew: true,
        isShaking: false,
        isFalling: true,
        fallDistance: emptyCount,
      }
      newRow--
    }
  }
  
  return newCells
}

const HitMoji = observer(() => {
  const { t } = useTranslation()
  const [level, setLevel] = useState(0)
  const [cells, setCells] = useState<Cell[]>([])
  const [moves, setMoves] = useState(INIT_MOVES)
  const [score, setScore] = useState(0)
  const [collected, setCollected] = useState<Record<string, number>>({})
  const [isAnimating, setIsAnimating] = useState(false)
  const [showResult, setShowResult] = useState<'win' | 'lose' | null>(null)
  const [resourcesEarned, setResourcesEarned] = useState<Record<string, number>>({})
  
  // Gamification state
  const [streak, setStreak] = useState(0)
  const [maxStreak, setMaxStreak] = useState(0)
  const [totalHits, setTotalHits] = useState(0)
  const [perfectMoves, setPerfectMoves] = useState(0) // 6+ matches
  const [multiplier, setMultiplier] = useState(1)
  const [floatingScores, setFloatingScores] = useState<FloatingScore[]>([])
  const [lastMatchSize, setLastMatchSize] = useState(0)
  const [showMultiplier, setShowMultiplier] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [comboText, setComboText] = useState<string | null>(null)
  const [lastClickTime, setLastClickTime] = useState(0)
  const [speedBonus, setSpeedBonus] = useState(1)
  const [chainReaction, setChainReaction] = useState(0)
  const [fallingCells, setFallingCells] = useState<Set<number>>(new Set())
  const [explosionCells, setExplosionCells] = useState<Set<number>>(new Set())

  const currentSet = hitLevels[level] || hitLevels[0]
  const levelInfo = getHitLevelInfo(level)
  // Más difícil: necesitas coleccionar más tipos y más cantidad
  const targetCollections = Math.min(currentSet.length, 5 + Math.floor(level / 2))
  const targetPerType = 8 + level * 2 // Cantidad mínima por tipo

  // Sonido wrapper
  const sound = (type: Parameters<typeof playSound>[0]) => {
    if (soundEnabled) playSound(type)
  }

  // Inicializar tablero
  const initBoard = useCallback(() => {
    const newCells: Cell[] = Array.from({ length: BOARD_SIZE * BOARD_SIZE }, (_, i) => ({
      id: i,
      icon: pickRandom(currentSet),
      isMatched: false,
      isNew: false,
      isShaking: false,
      isFalling: false,
      fallDistance: 0,
    }))
    setCells(newCells)
    setMoves(INIT_MOVES + Math.floor(level / 2))
    setScore(0)
    setCollected({})
    setShowResult(null)
    setResourcesEarned({})
    setChainReaction(0)
    setSpeedBonus(1)
    setLastClickTime(0)
    setStreak(0)
    setMaxStreak(0)
    setTotalHits(0)
    setPerfectMoves(0)
    setMultiplier(1)
    setFloatingScores([])
    setComboText(null)
  }, [currentSet, level])

  useEffect(() => {
    initBoard()
  }, [initBoard])

  // Verificar victoria/derrota
  useEffect(() => {
    if (showResult) return
    
    // Contar tipos que tienen suficiente cantidad
    const completedTypes = Object.entries(collected).filter(([, count]) => count >= targetPerType).length
    
    if (completedTypes >= targetCollections) {
      setShowResult('win')
      sound('win')
      vibrate([100, 50, 100, 50, 200])
      
      // Calcular recursos con bonus por performance
      const earned: Record<string, number> = {}
      const baseReward = Math.floor(score / 10) + level + 1
      const streakBonus = Math.floor(maxStreak / 3)
      const perfectBonus = perfectMoves * 2
      const chainBonus = chainReaction * 5
      const totalReward = baseReward + streakBonus + perfectBonus + chainBonus
      
      resources.forEach(r => {
        earned[r] = Math.floor(totalReward / resources.length) + (Math.random() > 0.5 ? 1 : 0)
      })
      setResourcesEarned(earned)
      
      Object.entries(earned).forEach(([icon, amount]) => {
        profileStore.addResource(icon, amount)
      })
      
      leaderboardStore.submitGameScore('hitmoji', score, level)
      gamificationStore.recordGameResult('hitmoji', score, level, true)
      mojilandStore.tryCompleteFromGame('hitmoji', score, level, true)
    } else if (moves <= 0) {
      setShowResult('lose')
      sound('lose')
      vibrate(200)
      if (score > 0) {
        leaderboardStore.submitGameScore('hitmoji', score, level)
        gamificationStore.recordGameResult('hitmoji', score, level, false)
        mojilandStore.tryCompleteFromGame('hitmoji', score, level, false)
      }
    }
  }, [moves, collected, targetCollections, targetPerType, score, showResult, level, maxStreak, perfectMoves, chainReaction, soundEnabled])

  // Actualizar multiplier basado en streak
  useEffect(() => {
    if (streak >= 10) {
      setMultiplier(4)
    } else if (streak >= 7) {
      setMultiplier(3)
    } else if (streak >= 4) {
      setMultiplier(2)
    } else {
      setMultiplier(1)
    }
    
    if (streak > maxStreak) {
      setMaxStreak(streak)
    }
    
    if (streak >= 4) {
      setShowMultiplier(true)
      setTimeout(() => setShowMultiplier(false), 1000)
    }
  }, [streak, maxStreak])

  // Añadir floating score
  const addFloatingScore = (index: number, points: number, emoji: string, isCombo: boolean) => {
    const row = Math.floor(index / BOARD_SIZE)
    const col = index % BOARD_SIZE
    const id = Date.now() + Math.random()
    
    setFloatingScores(prev => [...prev, {
      id,
      x: (col / BOARD_SIZE) * 100,
      y: (row / BOARD_SIZE) * 100,
      score: points,
      emoji,
      isCombo
    }])
    
    setTimeout(() => {
      setFloatingScores(prev => prev.filter(f => f.id !== id))
    }, 1000)
  }

  // Procesar un match y aplicar gravedad
  const processMatch = useCallback((currentCells: Cell[], clickIndex: number, isChain = false): { 
    newCells: Cell[], 
    matchCount: number, 
    matchIcon: string,
    hadMatch: boolean 
  } => {
    const matches = findMatches(clickIndex, currentCells, BOARD_SIZE)
    
    if (matches.length >= MIN_MATCH) {
      const matchIcon = currentCells[clickIndex].icon
      const matchCount = matches.length
      
      // Aplicar gravedad
      const newCells = applyGravity(currentCells, matches, currentSet)
      
      return { newCells, matchCount, matchIcon, hadMatch: true }
    }
    
    return { newCells: currentCells, matchCount: 0, matchIcon: '', hadMatch: false }
  }, [currentSet])

  // Buscar matches automáticos después de gravedad
  const findAutoMatches = useCallback((currentCells: Cell[]): number[] => {
    const allMatches: number[] = []
    const checked = new Set<number>()
    
    for (let i = 0; i < currentCells.length; i++) {
      if (!checked.has(i) && currentCells[i].icon) {
        const matches = findMatches(i, currentCells, BOARD_SIZE, new Set())
        if (matches.length >= MIN_MATCH) {
          matches.forEach(m => {
            if (!allMatches.includes(m)) allMatches.push(m)
            checked.add(m)
          })
        }
      }
    }
    
    return allMatches
  }, [])

  // Click en celda
  const handleCellClick = (index: number) => {
    if (isAnimating || showResult) return

    const matches = findMatches(index, cells, BOARD_SIZE)
    
    if (matches.length >= MIN_MATCH) {
      setIsAnimating(true)
      const matchIcon = cells[index].icon
      const matchCount = matches.length
      
      // Calcular speed bonus
      const now = Date.now()
      const timeSinceLastClick = now - lastClickTime
      let currentSpeedBonus = 1
      if (lastClickTime > 0 && timeSinceLastClick < SPEED_BONUS_THRESHOLD) {
        currentSpeedBonus = timeSinceLastClick < 500 ? 3 : timeSinceLastClick < 1000 ? 2 : 1.5
        setSpeedBonus(currentSpeedBonus)
      } else {
        setSpeedBonus(1)
      }
      setLastClickTime(now)
      
      // Calcular puntos con multiplicadores
      const basePoints = matchCount * (level + 1)
      const comboBonus = matchCount > 5 ? matchCount * 2 : 0
      const chainBonus = chainReaction > 0 ? chainReaction * 10 : 0
      const totalMultiplier = multiplier * currentSpeedBonus
      const points = Math.round((basePoints + comboBonus + chainBonus) * totalMultiplier)
      
      // Marcar celdas para explosión
      setExplosionCells(new Set(matches))
      
      // Determinar tipo de match y sonido
      if (matchCount >= 10) {
        sound('superCombo')
        vibrate([50, 30, 50, 30, 100, 50, 150])
        setComboText('💥 ULTRA COMBO!! 💥')
        setPerfectMoves(p => p + 2)
      } else if (matchCount >= 8) {
        sound('superCombo')
        vibrate([50, 30, 50, 30, 100])
        setComboText('🔥 MEGA COMBO! 🔥')
        setPerfectMoves(p => p + 1)
      } else if (matchCount >= 6) {
        sound('combo')
        vibrate([50, 30, 50])
        setComboText('⚡ COMBO! ⚡')
        setPerfectMoves(p => p + 1)
      } else if (matchCount >= 4) {
        sound('match')
        vibrate(50)
        setComboText('✨ Nice! ✨')
      } else {
        sound('pop')
        vibrate(30)
        if (currentSpeedBonus > 1) {
          setComboText(`⚡ Speed x${currentSpeedBonus}!`)
        } else {
          setComboText(null)
        }
      }
      
      // Actualizar stats
      setStreak(s => s + 1)
      setTotalHits(h => h + matchCount)
      setLastMatchSize(matchCount)
      
      // Marcar celdas como matched con animación de explosión
      setCells(prev => prev.map((cell, i) => 
        matches.includes(i) ? { ...cell, isMatched: true } : cell
      ))
      
      // Floating score con indicador de multiplicador
      const scoreText = totalMultiplier > 1 ? `${points} (x${totalMultiplier.toFixed(1)})` : points
      addFloatingScore(index, points, matchIcon, matchCount >= 6 || totalMultiplier > 1)
      
      setScore(prev => prev + points)
      setCollected(prev => ({
        ...prev,
        [matchIcon]: (prev[matchIcon] || 0) + matchCount
      }))
      
      // Bonus moves por combos grandes
      if (matchCount >= 10) {
        setMoves(prev => prev + 3)
        sound('powerup')
      } else if (matchCount >= 8) {
        setMoves(prev => prev + 2)
        sound('powerup')
      } else if (matchCount >= 6) {
        setMoves(prev => prev + 1)
      }
      
      setMoves(prev => prev - 1)
      
      // Clear combo text
      setTimeout(() => setComboText(null), 1000)
      
      // Animación de explosión, luego gravedad
      setTimeout(() => {
        setExplosionCells(new Set())
        
        // Aplicar gravedad
        const newCells = applyGravity(cells, matches, currentSet)
        setCells(newCells)
        
        // Marcar celdas que caen
        const falling = new Set<number>()
        newCells.forEach((cell, i) => {
          if (cell.isFalling) falling.add(i)
        })
        setFallingCells(falling)
        
        // Después de la caída, verificar reacciones en cadena
        setTimeout(() => {
          setFallingCells(new Set())
          
          // Limpiar flags de caída
          setCells(prev => prev.map(cell => ({ 
            ...cell, 
            isFalling: false, 
            fallDistance: 0,
            isNew: false 
          })))
          
          // Buscar matches automáticos (chain reaction)
          setTimeout(() => {
            const autoMatches = findAutoMatches(newCells)
            if (autoMatches.length >= MIN_MATCH) {
              // Hay chain reaction!
              setChainReaction(prev => prev + 1)
              sound('combo')
              vibrate([30, 20, 30])
              setComboText(`🔗 CHAIN x${chainReaction + 1}! 🔗`)
              
              // Procesar el chain automáticamente
              const chainIcon = newCells[autoMatches[0]].icon
              const chainPoints = Math.round(autoMatches.length * (level + 1) * (chainReaction + 2) * multiplier)
              
              setExplosionCells(new Set(autoMatches))
              addFloatingScore(autoMatches[0], chainPoints, chainIcon, true)
              setScore(prev => prev + chainPoints)
              setCollected(prev => ({
                ...prev,
                [chainIcon]: (prev[chainIcon] || 0) + autoMatches.length
              }))
              setTotalHits(h => h + autoMatches.length)
              
              setTimeout(() => {
                setExplosionCells(new Set())
                const chainCells = applyGravity(newCells, autoMatches, currentSet)
                setCells(chainCells)
                
                setTimeout(() => {
                  setCells(prev => prev.map(cell => ({ 
                    ...cell, 
                    isFalling: false, 
                    fallDistance: 0,
                    isNew: false 
                  })))
                  setIsAnimating(false)
                  setChainReaction(0)
                }, 300)
              }, 250)
            } else {
              setIsAnimating(false)
              setChainReaction(0)
            }
          }, 100)
        }, 300)
      }, 300)
    } else {
      // No hay match - shake y reset streak
      sound('wrong')
      vibrate(100)
      setStreak(0)
      setSpeedBonus(1)
      setComboText(null)
      
      // Shake las celdas cercanas
      const shakeCells = [index, ...getAdjacent(index, BOARD_SIZE)]
      setCells(prev => prev.map((cell, i) => ({
        ...cell,
        isShaking: shakeCells.includes(i)
      })))
      
      setTimeout(() => {
        setCells(prev => prev.map(cell => ({ ...cell, isShaking: false })))
      }, 300)
    }
  }

  // Shuffle con sonido
  const handleShuffle = () => {
    if (moves <= 0 || isAnimating) return
    sound('whoosh')
    vibrate(50)
    const shuffled = shuffleArray([...cells])
    setCells(shuffled.map((cell, i) => ({ 
      ...cell, 
      id: i, 
      isNew: true, 
      isShaking: false,
      isFalling: false,
      fallDistance: 0
    })))
    setMoves(prev => prev - 1)
    setStreak(0)
    setSpeedBonus(1)
    setTimeout(() => {
      setCells(prev => prev.map(c => ({ ...c, isNew: false })))
    }, 300)
  }

  // Bomb power-up
  const handleBomb = () => {
    if (moves <= 0 || isAnimating) return
    sound('explosion')
    vibrate([100, 50, 100])
    
    // Eliminar un tipo de emoji random
    const targetIcon = pickRandom(currentSet)
    const targetCells = cells
      .map((c, i) => c.icon === targetIcon ? i : -1)
      .filter(i => i >= 0)
    
    if (targetCells.length > 0) {
      setIsAnimating(true)
      setCells(prev => prev.map((cell, i) => 
        targetCells.includes(i) ? { ...cell, isMatched: true } : cell
      ))
      
      const points = targetCells.length * (level + 1)
      addFloatingScore(targetCells[0], points, '💣', true)
      setScore(prev => prev + points)
      setTotalHits(h => h + targetCells.length)
      
      setTimeout(() => {
        setCells(prev => prev.map((cell, i) => 
          targetCells.includes(i)
            ? { ...cell, icon: pickRandom(currentSet), isMatched: false, isNew: true }
            : cell
        ))
        setIsAnimating(false)
      }, 400)
    }
    
    setMoves(prev => prev - 1)
  }

  const handleLevelSelect = (newLevel: number) => {
    sound('click')
    setLevel(newLevel)
  }

  // Contar tipos completados (con suficiente cantidad)
  const completedTypes = Object.entries(collected).filter(([, count]) => count >= targetPerType).length
  const collectedCount = Object.keys(collected).length

  return (
    <div className="min-h-screen hitmoji-bg flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 game-header">
        <div className="max-w-md mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link 
                to="/"
                className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70 transition-colors text-gray-700"
              >
                <span className="text-sm">←</span>
              </Link>
              <div className="flex items-center gap-1">
                {streak >= 4 && <span className="text-lg animate-bounce">🔥</span>}
                {streak >= 7 && <span className="text-lg animate-bounce" style={{ animationDelay: '0.1s' }}>🔥</span>}
                {streak >= 10 && <span className="text-lg animate-bounce" style={{ animationDelay: '0.2s' }}>🔥</span>}
                <span className={`font-bold text-xl transition-all ${
                  streak >= 10 
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 animate-pulse scale-110' 
                    : streak >= 7 
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500' 
                      : streak >= 4 
                        ? 'text-orange-600' 
                        : 'text-gray-800'
                }`}>
                  HitMoji
                </span>
                {streak >= 4 && <span className="text-lg animate-bounce" style={{ animationDelay: '0.15s' }}>🔥</span>}
                {streak >= 7 && <span className="text-lg animate-bounce" style={{ animationDelay: '0.25s' }}>🔥</span>}
                {streak >= 10 && <span className="text-lg animate-bounce" style={{ animationDelay: '0.3s' }}>🔥</span>}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Link to="/leaderboard" className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70">
                <span>🏆</span>
              </Link>
              <button 
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70"
              >
                <span>{soundEnabled ? '🔊' : '🔇'}</span>
              </button>
            </div>
          </div>
          
          {/* Stats Bar */}
          <div className="flex justify-between items-center mt-2 gap-2">
            <div className="flex items-center gap-1.5">
              <div className={`px-2 py-1 rounded-lg text-xs font-bold ${moves <= 3 ? 'bg-red-500 animate-pulse text-white' : 'bg-white/50 border border-white/60'}`}>
                <span className={moves <= 3 ? 'text-white/80' : 'text-gray-500'}>💣</span>
                <span className={moves <= 3 ? 'text-white ml-1' : 'text-gray-800 ml-1'}>{moves}</span>
              </div>
              <div className="px-2 py-1 rounded-lg bg-white/50 border border-white/60 text-xs font-bold">
                <span className="text-gray-500">⭐</span>
                <span className="text-gray-800 ml-1">{score}</span>
              </div>
            </div>
            
            {/* Progress */}
            <div className="flex-1 mx-2">
              <div className="h-2 bg-white/40 rounded-full overflow-hidden border border-white/50">
                <div 
                  className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all duration-300"
                  style={{ width: `${Math.min(100, (completedTypes / targetCollections) * 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-500 text-center mt-0.5">
                {completedTypes}/{targetCollections} complete ({targetPerType}+ each)
              </p>
            </div>
            
            {/* Streak/Multiplier */}
            <div className={`px-3 py-1.5 rounded-xl text-sm font-bold transition-all duration-300 ${
              multiplier >= 4 
                ? 'bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 scale-125 text-white shadow-lg shadow-orange-500/50 animate-pulse' 
                : multiplier >= 3 
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 scale-115 text-white shadow-lg shadow-orange-400/40' 
                  : multiplier >= 2 
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 scale-110 text-white shadow-md' 
                    : 'bg-white/50 border border-white/60'
            }`}>
              <span className={multiplier > 1 ? 'animate-bounce inline-block' : ''}>🔥</span>
              <span className={multiplier > 1 ? 'text-white ml-1' : 'text-gray-800 ml-1'}>{streak}</span>
              {multiplier > 1 && (
                <span className={`ml-1 ${multiplier >= 4 ? 'text-white font-black' : 'text-yellow-200'}`}>
                  ×{multiplier}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>
      
      <div className="flex-1 max-w-md mx-auto px-3 py-3 w-full relative">
        {/* Combo Text */}
        {comboText && (
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-50 animate-bounce">
            <div className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full text-white font-bold text-lg shadow-lg">
              {comboText}
            </div>
          </div>
        )}

        {/* Multiplier Popup */}
        {showMultiplier && multiplier > 1 && (
          <div className="absolute top-12 left-1/2 -translate-x-1/2 z-50 animate-ping">
            <div className="px-3 py-1 bg-purple-500 rounded-full text-white font-bold">
              ×{multiplier} MULTIPLIER!
            </div>
          </div>
        )}

        {/* Collected Emojis con progreso */}
        {Object.keys(collected).length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5 mb-2">
            {Object.entries(collected).map(([icon, count]) => {
              const isComplete = count >= targetPerType
              const progress = Math.min(100, (count / targetPerType) * 100)
              return (
                <div 
                  key={icon} 
                  className={`relative flex items-center gap-1 px-2 py-1 rounded-lg text-sm transition-all ${
                    isComplete 
                      ? 'bg-white/30 border-2 border-green-400' 
                      : 'bg-white/20 border border-white/30'
                  }`}
                >
                  {/* Progress bar background */}
                  {!isComplete && (
                    <div 
                      className="absolute inset-0 bg-white/20 rounded-lg transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  )}
                  <span className="relative text-lg">{icon}</span>
                  <span className={`relative text-xs font-bold ${isComplete ? 'text-green-300' : 'text-white/80'}`}>
                    {count}/{targetPerType}
                  </span>
                  {isComplete && <span className="text-green-300 text-xs">✓</span>}
                </div>
              )
            })}
          </div>
        )}

        {/* Game Board */}
        <div className="glass-card p-2 mb-3 relative">
          {/* Floating Scores */}
          {floatingScores.map(fs => (
            <div
              key={fs.id}
              className="absolute pointer-events-none z-50 animate-float-up"
              style={{ left: `${fs.x}%`, top: `${fs.y}%` }}
            >
              <div className={`px-2 py-1 rounded-full font-bold text-sm ${
                fs.isCombo 
                  ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white scale-125' 
                  : 'bg-white/90 text-gray-800'
              }`}>
                +{fs.score} {fs.emoji}
              </div>
            </div>
          ))}

          <div 
            className="grid gap-0.5"
            style={{ gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)` }}
          >
            {cells.map((cell, index) => {
              const isExploding = explosionCells.has(index)
              const isFalling = cell.isFalling && cell.fallDistance > 0
              
              return (
                <button
                  key={index}
                  onClick={() => handleCellClick(index)}
                  disabled={isAnimating || !!showResult}
                  className={`
                    aspect-square rounded-md flex items-center justify-center
                    transition-all ease-out
                    ${isExploding 
                      ? 'bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 scale-125 animate-pulse' 
                      : cell.isMatched 
                        ? 'bg-yellow-400/60 scale-0 opacity-0' 
                        : 'bg-white/10 hover:bg-white/25 hover:scale-110 active:scale-95 cursor-pointer'
                    }
                    ${cell.isNew ? 'animate-bounce-in' : ''}
                    ${cell.isShaking ? 'animate-shake' : ''}
                    ${isFalling ? 'animate-fall' : ''}
                  `}
                  style={{
                    transitionDuration: isExploding ? '150ms' : isFalling ? `${200 + cell.fallDistance * 50}ms` : '200ms',
                    animationDelay: isFalling ? `${(index % BOARD_SIZE) * 30}ms` : '0ms',
                  }}
                >
                  <span className={`select-none transition-transform ${
                    isExploding ? 'text-2xl sm:text-3xl scale-150' : 'text-xl sm:text-2xl'
                  }`}>
                    {isExploding ? '💥' : cell.icon}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Power Ups */}
        <div className="flex justify-center gap-2 mb-3">
          <button 
            className="flex items-center gap-1 px-3 py-2 rounded-xl bg-white/15 hover:bg-white/25 transition-all active:scale-95" 
            onClick={handleShuffle}
            disabled={moves <= 0 || isAnimating || !!showResult}
          >
            <span className="text-lg">🔄</span>
            <span className="text-xs text-white/70">Shuffle</span>
          </button>
          <button 
            className="flex items-center gap-1 px-3 py-2 rounded-xl bg-gradient-to-r from-red-500/50 to-orange-500/50 hover:from-red-500/70 hover:to-orange-500/70 transition-all active:scale-95" 
            onClick={handleBomb}
            disabled={moves <= 0 || isAnimating || !!showResult}
          >
            <span className="text-lg">💣</span>
            <span className="text-xs text-white/70">Bomb</span>
          </button>
        </div>

        {/* Level Selector */}
        <div className="glass-card p-2">
          <div className="flex justify-center gap-1 flex-wrap">
            {hitLevels.map((_, idx) => {
              const isActive = idx === level
              const info = getHitLevelInfo(idx)
              return (
                <button
                  key={idx}
                  onClick={() => handleLevelSelect(idx)}
                  className={`
                    w-9 h-9 rounded-lg flex items-center justify-center text-lg
                    transition-all duration-150 cursor-pointer
                    ${isActive 
                      ? 'bg-white scale-110 shadow-lg' 
                      : 'bg-white/10 hover:bg-white/25'
                    }
                  `}
                >
                  {info.emoji}
                </button>
              )
            })}
          </div>
        </div>

        {/* Mini Leaderboard - Live score */}
        <div className="mt-3">
          <GameLeaderboard 
            game="hitmoji"
            gameName="HitMoji"
            emoji="💥"
            currentScore={score}
            gradient="from-orange-500 to-red-500"
            limit={5}
          />
        </div>

        {/* Result Modal */}
        {showResult && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl animate-bounce-in">
              <span className="text-6xl block mb-3">
                {showResult === 'win' ? '🎉' : '💥'}
              </span>
              <h2 className="font-title text-2xl text-orange-600 mb-1">
                {showResult === 'win' ? t('common.levelComplete') : t('common.gameOver')}
              </h2>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 my-4">
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-orange-500">{score}</p>
                  <p className="text-xs text-gray-500">{t('common.score')}</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-purple-500">{maxStreak}</p>
                  <p className="text-xs text-gray-500">{t('hitmoji.maxStreak')}</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-green-500">{totalHits}</p>
                  <p className="text-xs text-gray-500">{t('hitmoji.totalHits')}</p>
                </div>
              </div>
              
              {/* Resources */}
              {showResult === 'win' && Object.keys(resourcesEarned).length > 0 && (
                <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-2">{t('hitmoji.resourcesEarned')}</p>
                  <div className="flex justify-center gap-3">
                    {Object.entries(resourcesEarned).map(([icon, amount]) => (
                      <div key={icon} className="flex items-center gap-1">
                        <span className="text-xl">{icon}</span>
                        <span className="font-bold text-green-600">+{amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 justify-center">
                <button
                  onClick={initBoard}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
                >
                  🔄 {t('common.retry')}
                </button>
                {showResult === 'win' && level < hitLevels.length - 1 && (
                  <button
                    onClick={() => {
                      sound('levelUp')
                      setLevel(level + 1)
                      setShowResult(null)
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-semibold"
                  >
                    {t('common.nextArrow')}
                  </button>
                )}
                <Link
                  to="/leaderboard"
                  className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl font-semibold"
                >
                  🏆
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes float-up {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-60px) scale(1.2); }
        }
        .animate-float-up {
          animation: float-up 0.8s ease-out forwards;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.2s ease-in-out 2;
        }
        @keyframes pop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-pop {
          animation: pop 0.3s ease-out;
        }
      `}</style>
    </div>
  )
})

export default HitMoji
